import {
    CompareContractsOutput,
    ContractDelta,
    DeltaStack,
    RowData
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';

import { isEqual, isNil, isUndefined } from 'lodash';

type RowSet<T> = {
    [name: string]: T[];
};

type PairedData = {
    [column: string]: {
        left: string;
        right: string;
    };
};

export type TableRow = {
    data: RowData;
    changedData: PairedData;
    contextData: RowData;
    stack: DeltaStack;
    isExactMatch: boolean;
};

export type TableData = {
    matchingColumns: string[];
    nonMatchingColumns: string[];
    contextColumns: string[];
    rows: TableRow[];
};

function getOrCreate<T>(dict: RowSet<T>, key: string): T[] {
    if (!(key in dict)) {
        dict[key] = [];
    }
    return dict[key];
}

type DeltaSide = 'left' | 'right';
const SIDES: DeltaSide[] = ['left', 'right'];

function pushAll(changeSet: RowSet<TableRow>, delta: ContractDelta, side: DeltaSide, context: RowData) {
    makeTableRows(delta, side, context).forEach((row) => getOrCreate(changeSet, row.table).push(row));
}

function makeTableRows(delta: ContractDelta, side: DeltaSide, context: RowData): (TableRow & { table: string })[] {
    return delta[side].map((row) => ({
        data: row.data,
        changedData: {},
        contextData: context,
        stack: delta.stack,
        table: row.table,
        isExactMatch: false
    }));
}

type TableChanges = { [table: string]: TableData };

function createDiffRows(changedRows: RowSet<ChangedRow>): TableChanges {
    const diffRows: TableChanges = {};

    Object.keys(changedRows).forEach((table) => {
        const changedCols = new Set<string>();
        const comparedCols = new Set<string>();
        const contextCols = new Set<string>();
        const rows: TableRow[] = [];

        changedRows[table].forEach(({ leftRow, rightRow, stack, contextData, isExactMatch }) => {
            const left = leftRow.data;
            const right = rightRow.data;
            const allKeys = new Set([...Object.keys(left), ...Object.keys(right)]);

            const diffData: { [name: string]: string } = {};
            const changedData: PairedData = {};

            allKeys.forEach((key) => {
                if (left[key] === right[key]) {
                    diffData[key] = left[key];
                } else {
                    changedData[key] = {
                        left: left[key],
                        right: right[key]
                    };
                }
            });

            rows.push({
                stack: stack,
                changedData: changedData,
                data: RowData.fromJson(diffData),
                contextData: contextData,
                isExactMatch: isExactMatch
            });

            Object.keys(diffData).forEach((col) => changedCols.add(col));
            Object.keys(changedData).forEach((col) => comparedCols.add(col));
            Object.keys(contextData).forEach((col) => contextCols.add(col));
        });

        diffRows[table] = {
            matchingColumns: Array.from(changedCols),
            nonMatchingColumns: Array.from(comparedCols),
            contextColumns: Array.from(contextCols),
            rows: rows
        };
    });

    return diffRows;
}

function createRows(rowSet: RowSet<TableRow>): TableChanges {
    const result: TableChanges = {};

    Object.keys(rowSet).forEach((table) => {
        const rows: TableRow[] = [];
        const columns = new Set<string>();
        const contextColumns = new Set<string>();
        rowSet[table].forEach((row) => {
            rows.push({
                data: RowData.fromJson(row.data),
                changedData: {},
                contextData: row.contextData,
                stack: row.stack,
                isExactMatch: false
            });
            Object.keys(row.data).forEach((column) => columns.add(column));
            Object.keys(row.contextData).forEach((column) => contextColumns.add(column));
        });
        result[table] = {
            matchingColumns: Array.from(columns),
            contextColumns: Array.from(contextColumns),
            nonMatchingColumns: [],
            rows: rows
        };
    });

    return result;
}

export type TableResult = {
    left?: TableData;
    center?: TableData;
    right?: TableData;
};

export type ExtractResult = {
    [table: string]: TableResult;
};

type TableKeys = Map<string, string[]>;
type RowIndex = Map<string, Map<string, RowData>>;

/**
 * Infers the key columns for each table and creates a lookup mapping each
 * row's key to the row data.
 * @param comparison The comparison to create a lookup for.
 */
export function createRowLookup(comparison: CompareContractsOutput): [TableKeys, RowIndex] {
    const tableKeys = new Map<string, string[]>();

    comparison.deltas.forEach((delta) => {
        delta.stack.forEach((stackItem) => {
            SIDES.forEach((side) => {
                const table = stackItem[side].table;
                const keys = Object.keys(stackItem[side].data).sort();

                if (tableKeys.has(table)) {
                    if (!isEqual(tableKeys.get(table), keys)) {
                        throw new Error(`Multiple keys for ${table}: ${keys} vs ${tableKeys.get(table)}`);
                    }
                } else {
                    tableKeys.set(table, keys);
                }
            });
        });
    });

    const index: Map<string, Map<string, RowData>> = new Map();

    comparison.deltas.forEach((delta) => {
        SIDES.forEach((side) => {
            delta[side].forEach((item) => {
                if (tableKeys.has(item.table)) {
                    if (!index.has(item.table)) {
                        index.set(item.table, new Map());
                    }
                    const keyCols = tableKeys.get(item.table)!;
                    index.get(item.table)!.set(getKey(keyCols, item.data), item.data);
                }
            });
        });
    });

    return [tableKeys, index];
}

function getKey(keyCols: string[], row: RowData): string {
    return keyCols.map((keyCol) => row[keyCol]).join('+');
}

/**
 * Constructs a row context from the given stack using a table key map
 * and a row index.
 * @param stack The stack to construct a context for.
 * @param tableKeys Map of tables to their compound keys.
 * @param index Map of row keys to their full data.
 */
export function createRowContext(stack: DeltaStack, tableKeys: TableKeys, index: RowIndex): RowData {
    const context: Record<string, string> = {};
    stack.forEach((pairedKeys) => {
        // assume these are the same, we validated this earlier
        const table = pairedKeys.left.table;
        const keyCols = tableKeys.get(table)!;

        const leftKey = getKey(keyCols, pairedKeys.left.data);
        const leftData = index.get(table)?.get(leftKey) ?? {};

        const rightKey = getKey(keyCols, pairedKeys.right.data);
        const rightData = index.get(table)?.get(rightKey) ?? {};

        const sharedKeys = new Set([...Object.keys(leftData), ...Object.keys(rightData)]);

        sharedKeys.forEach((sharedKey) => {
            if (leftData[sharedKey] === rightData[sharedKey]) {
                context[sharedKey] = leftData[sharedKey];
            }
        });
    });
    return RowData.fromJson(context);
}

type ChangedRow = {
    leftRow: TableRow;
    rightRow: TableRow;
    stack: DeltaStack;
    contextData: RowData;
    isExactMatch: boolean;
};

export function extractTables(comparison: CompareContractsOutput): ExtractResult {
    const addedRows: RowSet<TableRow> = {};
    const changedRows: RowSet<ChangedRow> = {};
    const removedRows: RowSet<TableRow> = {};

    const [tableKeys, rowIndex] = createRowLookup(comparison);

    comparison.deltas.forEach((delta) => {
        const context = createRowContext(delta.stack, tableKeys, rowIndex);
        if (delta.left.length === 1 && delta.right.length === 1) {
            if (delta.left[0].table !== delta.right[0].table) {
                throw new Error(
                    'Found change with two different tables: ' + `${delta.left[0].table} and ${delta.right[0].table}.`
                );
            }
            getOrCreate(changedRows, delta.left[0].table).push({
                leftRow: makeTableRows(delta, 'left', context)[0],
                rightRow: makeTableRows(delta, 'right', context)[0],
                stack: delta.stack,
                contextData: context,
                isExactMatch: delta.isExactMatch ?? false
            });
        } else if (delta.left.length === 0) {
            pushAll(addedRows, delta, 'right', context);
        } else if (delta.right.length === 0) {
            pushAll(removedRows, delta, 'left', context);
        } else {
            throw new Error('Found multiple rows on both sides of a delta.');
        }
    });

    const changedResult = createDiffRows(changedRows);
    const addedResult = createRows(addedRows);
    const removedResult = createRows(removedRows);

    const result: ExtractResult = {};

    new Set([...Object.keys(changedResult), ...Object.keys(addedResult), ...Object.keys(removedResult)]).forEach(
        (table) => {
            result[table] = {
                left: removedResult[table],
                center: changedResult[table],
                right: addedResult[table]
            };
        }
    );

    return result;
}

export interface Node {
    table: string;
    parent?: Node;
    children: string[];
}

export interface Lookup {
    [table: string]: Node;
}

function addLookup(table: string, prev: Node | undefined, lookup: Lookup) {
    const node: Node = lookup[table] ?? {
        table: table,
        parent: prev,
        children: []
    };
    if (node.parent !== prev) {
        throw new Error(`Found multiple parents for ${table}: ` + `${node.parent?.table} and ${prev?.table}.`);
    }
    if (!isUndefined(prev) && !prev.children.includes(table)) {
        prev.children.push(table);
    }
    lookup[table] = node;
    return node;
}

export function createLookup(comparison: CompareContractsOutput): Lookup {
    const lookup: Lookup = {};
    comparison.deltas.forEach((delta) => {
        const leafTables = new Set([...delta.left.map((item) => item.table), ...delta.right.map((item) => item.table)]);
        const parents = delta.stack
            .map((item) => {
                if (item.left.table !== item.right.table) {
                    throw new Error(
                        'Found stack item with different tables: ' + `${item.left.table} and ${item.right.table}.`
                    );
                }
                return item.left.table;
            })
            .reverse();

        let prev: Node | undefined = undefined;
        parents.forEach((table) => {
            prev = addLookup(table, prev, lookup);
        });
        leafTables.forEach((table) => {
            addLookup(table, prev, lookup);
        });
    });
    const rootNodes = Object.keys(lookup).filter((key) => isNil(lookup[key].parent));
    if (rootNodes.length > 1) {
        throw new Error(`Found multiple root tables: ${rootNodes}.`);
    } else if (rootNodes.length === 0) {
        throw new Error('Found no root tables.');
    }
    return lookup;
}

export function createStack(lookup: Lookup, table: string): string[] {
    const stack: string[] = [];
    let node: Node | undefined = lookup[table];
    while (!isUndefined(node)) {
        stack.push(node.table);
        node = node.parent;
    }
    return stack.reverse();
}

export function validateComparison(comparison: CompareContractsOutput): string[] {
    const errors = new Set<string>();

    let rootTable: string | undefined;

    comparison.deltas.forEach((delta) => {
        delta.stack.forEach((item) => {
            if (item.left.table !== item.right.table) {
                errors.add('Found stack item with different tables.');
            }
        });

        if (delta.stack.length > 0) {
            const localRoot = delta.stack[delta.stack.length - 1].left.table;
            if (isNil(rootTable)) {
                rootTable = localRoot;
            }

            if (rootTable !== localRoot) {
                errors.add('Found multiple root tables.');
            }
        }

        if (delta.left.length > 0 && delta.right.length > 0) {
            if (delta.left.length !== 1 || delta.right.length !== 1) {
                errors.add('Found multiple rows on both sides of a delta.');
            } else if (delta.left[0].table !== delta.right[0].table) {
                errors.add('Found change with two different tables.');
            }
        }
    });

    return Array.from(errors);
}
